// Guards de concorrência do fluxo MAD (CON-01/02/03/04) — espelho de
// normal/concurrency.ts (ports 1:1 de index.tsx:2150-2254): snapshot IDÊNTICO ao
// original (incl. participants_approval, index.tsx:2174). No MAD a dupla age sobre o
// MESMO pool em telas separadas, então o guard em lote também protege a bonificação
// (CON-04, via shouldGuardBatchOnDouble). As dependências de IO/UI são INJETADAS nas
// fábricas; nenhum import de React (AD-002) nem de normal/ (isolamento AD-001).

import { MadRuleContext, isMadTask } from './context';

// CON-01 — snapshot dos campos que definem o estado de ação da task, com ids
// normalizados para string.
export interface TaskActionStateSnapshot {
  status: string | null;
  multiplicador: number | null;
  stageNumber: number | null;
  original_manager: string | null;
  evaluator: string[];
  non_evaluator: string[];
  prosseguir_gst: string | null;
  prosseguir_idGestor: string | null;
  prosseguir_who_approved: string | null;
  prosseguir_managers: string[];
  mad_manager_appreciated: string[];
  participants_approval: any;
}

export function getTaskActionStateSnapshot(task: any): TaskActionStateSnapshot | null {
  if (!task || typeof task !== 'object') return null;

  const normalizeIdArray = (value: any): string[] => {
    if (!Array.isArray(value)) return [];

    return value.map(item => (typeof item === 'string' || typeof item === 'number' ? String(item) : '')).filter(Boolean);
  };

  const normalizeId = (value: any): string | null =>
    typeof value === 'string' || typeof value === 'number' ? String(value) : null;

  return {
    status: typeof task.status === 'string' ? task.status : null,
    multiplicador: typeof task.multiplicador === 'number' ? task.multiplicador : null,
    stageNumber: typeof task.stageNumber === 'number' ? task.stageNumber : null,
    original_manager: normalizeId(task.original_manager),
    evaluator: normalizeIdArray(task.evaluator),
    non_evaluator: normalizeIdArray(task.non_evaluator),
    prosseguir_gst: typeof task.prosseguir_gst === 'string' ? task.prosseguir_gst : null,
    prosseguir_idGestor: normalizeId(task.prosseguir_idGestor),
    prosseguir_who_approved: normalizeId(task.prosseguir_who_approved),
    prosseguir_managers: normalizeIdArray(task.prosseguir_managers),
    mad_manager_appreciated: normalizeIdArray(task.mad_manager_appreciated),
    participants_approval: !!task.participants_approval ? task.participants_approval : null,
  };
}

// Divergência entre a task da tela e a fresca do servidor (comparação por
// JSON.stringify dos snapshots, como no original).
export function hasTaskConcurrentChange(taskFromScreen: any, taskFresh: any): boolean {
  const taskFromScreenSnapshot = getTaskActionStateSnapshot(taskFromScreen);
  const taskFreshSnapshot = getTaskActionStateSnapshot(taskFresh);

  if (!taskFromScreenSnapshot || !taskFreshSnapshot) return false;

  return JSON.stringify(taskFromScreenSnapshot) !== JSON.stringify(taskFreshSnapshot);
}

// Mensagens EXATAS do monólito (fazem parte do comportamento observável).
export const CONCURRENT_TASK_MESSAGE =
  'Esta realização foi atualizada por outro gestor enquanto você apreciava. A ação foi cancelada para evitar sobreposição.';
export const CONCURRENT_BATCH_MESSAGE =
  'A lista de realizações foi atualizada por outro gestor enquanto você apreciava. A ação foi cancelada e os dados serão recarregados para evitar conclusões incorretas.';

export interface GuardTaskDeps {
  // loadData do monólito: com id devolve a task fresca; sem argumento recarrega a lista.
  reload: (taskId?: string) => Promise<any>;
  // setShowConcurrentActionModal do monólito.
  onConflict: (message: string) => void;
}

// CON-02 — guard individual: rebusca a task; divergiu → conflito + recarga + aborta
// (null). Sem divergência devolve a task FRESCA para a ação prosseguir sobre ela.
export function makeGuardTask(deps: GuardTaskDeps) {
  return async function guardTaskAgainstConcurrentUpdate(taskFromScreen: any): Promise<any> {
    if (!taskFromScreen?._id) return null;

    const taskFresh = await deps.reload(taskFromScreen._id);

    if (!taskFresh) return null;

    if (hasTaskConcurrentChange(taskFromScreen, taskFresh)) {
      deps.onConflict(CONCURRENT_TASK_MESSAGE);
      void deps.reload();
      return null;
    }

    return taskFresh;
  };
}

export interface GuardBatchDeps {
  // getTasksManager do monólito, com os parâmetros da fase fechados pelo chamador.
  fetchAll: () => Promise<any[] | undefined>;
  onConflict: (message: string) => void;
}

// CON-03 — guard em lote: rebaixa a lista inteira do gestor/etapa; qualquer task
// local ausente ou divergente no servidor aborta ({ ok: false }). Não recarrega
// aqui: o chamador (updateAllTasks) já recarrega no finally.
export function makeGuardBatch(deps: GuardBatchDeps) {
  return async function guardBatchAgainstConcurrentUpdate(
    ctx: MadRuleContext,
    localTasks: any[],
  ): Promise<{ ok: boolean; freshTasks: any[] }> {
    const safeLocal = Array.isArray(localTasks) ? localTasks : [];

    if (!ctx.activeStageObj) {
      return { ok: true, freshTasks: safeLocal };
    }

    const freshTasks = await deps.fetchAll();

    if (!Array.isArray(freshTasks)) {
      return { ok: true, freshTasks: safeLocal };
    }

    const freshById = new Map<string, any>(freshTasks.map((task: any) => [task._id, task]));

    const hasDivergence = safeLocal.some((local: any) => {
      const fresh = freshById.get(local._id);
      if (!fresh) return true;
      return hasTaskConcurrentChange(local, fresh);
    });

    if (hasDivergence) {
      deps.onConflict(CONCURRENT_BATCH_MESSAGE);
      return { ok: false, freshTasks };
    }

    return { ok: true, freshTasks };
  };
}

// CON-04 — gatilho do guard em lote NA BONIFICAÇÃO MAD — port de index.tsx:2841-2858.
// Quando `canPromote` indica que ainda há slot de promoção livre, a bonificação
// ficaria 'dobrado'; como a dupla age no mesmo pool em telas separadas, esse "há slot"
// pode estar desatualizado (o parceiro pode ter fechado a cota). Só então (slot livre
// + task MAD) validamos a lista contra o servidor. `ctx` compõe a assinatura para
// simetria com os demais guards e futura extensão; a decisão CON-04 depende apenas de
// canPromote + isMadTask.
export function shouldGuardBatchOnDouble(
  ctx: MadRuleContext,
  task: any,
  canPromoteResult: boolean,
): boolean {
  void ctx;
  return !!canPromoteResult && isMadTask(task);
}
